import { FC, useState, useEffect, useRef, ChangeEvent } from 'react';
import styled from 'styled-components';
import Colors from '../../../ui/global/Colors';
import Fonts from '../../../ui/global/Fonts';
import InputSimple from '../../../ui/components/InputSimple';
import Button from '../../../ui/components/Button';
import Toggle from '../../../ui/components/Toggle';
import TopBar from '../../website/TopBar';
import Hero from '../../website/Hero';
import GameGallery from '../components/GameGallery';
import GlobalMessage from '../../pricing/GlobalMessage';
import Tooltip from '../../../ui/components/Tooltip';
import { useCreditsContext } from '../../pricing/CreditsContext';
import CursorEffect from '../../../ui/components/CursorEffect';
import Footer from '../../website/Footer';

const Root = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    height: 100svh;
    background-color: ${Colors.black};
    color: ${Colors.white};
    padding: 5px 5px 0 5px;
    // margin-top: 54px;
    gap: 0px;
    overflow: hidden;
`;

const InitialContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    flex: 1;
    width: 100%;
    text-align: center;
    margin: 0;
    margin-bottom: 10px;
    gap: 20px;
    overflow-y: auto;
    overflow-x: hidden;

    /* Custom Scrollbar */
    &::-webkit-scrollbar {
        width: 8px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: #3f3f46;
        border-radius: 10px;
    }
    &::-webkit-scrollbar-thumb:hover {
        background: #52525b;
    }
`;

const InitialInputContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    max-width: 940px;
    font-family: ${Fonts.lexendMedium.fontFamily};
    font-weight: ${Fonts.lexendMedium.fontWeight};
    font-size: 22px;
    line-height: 1.2em;
    border-radius: 14px;
    background-color: ${Colors.grey9};
    border: 1px solid ${Colors.grey8};
    padding: 6px;
    color: ${Colors.white};
    gap: 10px;
    margin: 30px 10px;
    transition: border 0.2s ease;

    &:hover {
        border: 1px solid ${Colors.grey7};
    }

    @media (max-width: 400px) {
        font-size: 18px;
    }
`;

const DebugContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    gap: 20px;
`;

const ToggleContainer = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    width: 100%;
    font-family: ${Fonts.lexendMedium.fontFamily};
    font-weight: ${Fonts.lexendMedium.fontWeight};
    font-size: 16px;
    line-height: 1.2em;
    color: ${Colors.white};
    gap: 10px;
`;

interface GameInitialProps {
    config: any;
    instruction: string;
    onInstructionChange: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    onGenerateFromInput: (instruction: string) => void;
    aiToggle: boolean;
    onAiToggleChange: () => void;
    onDebugTemplateClick: () => void;
    user: any;
}

const GameInitial: FC<GameInitialProps> = ({
    config,
    instruction,
    onInstructionChange,
    onGenerateFromInput,
    aiToggle,
    onAiToggleChange,
    onDebugTemplateClick,
    user,
}) => {
    const { isOutOfCredits } = useCreditsContext();
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const handleScrollToTop = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const tabs = [
        { name: 'My Games' },
        { name: 'Community' },
    ];

    const input = (
        <>
            <InitialInputContainer>
                <InputSimple 
                    placeholder="Create a game..." 
                    value={instruction} 
                    onChange={onInstructionChange} 
                    // onEnterPress={() => {
                    //     if (instruction.trim() !== '') {
                    //         onGenerateFromInput(instruction);
                    //     }
                    // }}
                    textAlign='left'
                    colorMode='dark'
                />
                {/* <Tooltip text='Create Game' sub_text='3 credits' colorMode="dark" > */}
                    <Button 
                        size="tiny" 
                        color="white" 
                        icon="send" 
                        onClick={() => onGenerateFromInput(instruction)} 
                        disabled={instruction === '' || isOutOfCredits.game_create_limited}
                    />
                {/* </Tooltip> */}
            </InitialInputContainer>
            {(config.DEBUG_CODE_MODEL_TOGGLE || config.DEBUG_CODE_TEMPLATE_BUTTON) && (
                <DebugContainer>
                    {config.DEBUG_CODE_MODEL_TOGGLE && (
                        <ToggleContainer>
                            Fast
                            <Toggle 
                                checked={aiToggle}
                                label={{ text: 'Pro', placement: 'end' }}
                                colorMode='dark'
                                disabled={false}
                                onChange={onAiToggleChange}
                            />
                        </ToggleContainer>
                    )}
                    {config.DEBUG_CODE_TEMPLATE_BUTTON && (
                        <Button
                            size="tiny"
                            color="black"
                            text="DEBUG"
                            onClick={onDebugTemplateClick}
                        />
                    )}
                </DebugContainer>
            )}
        </>
    );

    return (
        <Root>
            <GlobalMessage config={config} colorMode="dark" />
            <TopBar type='Game' colorMode='dark'/>
            <InitialContainer ref={scrollContainerRef}>
                <Hero 
                    title="AI Game Maker" 
                    subtitle="Idea to playable game in seconds."
                    children={input} 
                />
                <GameGallery colormode="dark" tabs={tabs} />
                <CursorEffect colorMode="dark"/>
            </InitialContainer>
            <Footer colorMode='dark' onLogoClick={handleScrollToTop} page='games'/>
        </Root>
    );
};

export default GameInitial;